import React, { useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import inviteMemberIcon from '@/assets/icons/invite-member.svg';

const emailSchema = z.string().email();

export const parseInviteEmails = (value: string): string[] => {
  return value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);
};

export const validateInviteEmails = (value: string): { emails: string[]; error: string | null } => {
  const emails = parseInviteEmails(value);

  if (!emails.length) {
    return { emails: [], error: 'Enter at least one email address' };
  }

  const invalidEmails = emails.filter((email) => !emailSchema.safeParse(email).success);

  if (invalidEmails.length) {
    return {
      emails: [],
      error: `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`
    };
  }

  return { emails, error: null };
};

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (emails: string[]) => void | Promise<void>;
  loading?: boolean;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ isOpen, onClose, onInvite, loading = false }) => {
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setEmailInput('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleInvite = async () => {
    const { emails, error: validationError } = validateInviteEmails(emailInput);

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError(null);
      await onInvite(emails);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send invitation. Please try again.');
    }
  };

  const handleInputChange = (value: string) => {
    setEmailInput(value);

    if (!error) {
      return;
    }

    const { error: validationError } = validateInviteEmails(value);
    setError(validationError);
  };

  return (
    <div className="fixed w-screen h-screen top-0 left-0 bg-[rgba(82,80,96,0.5)] backdrop-blur-[8px] z-50 flex items-center justify-center">
      <div className="bg-white rounded-[16px] w-[327px] overflow-hidden">
        <div className="w-full py-7 px-7 md:p-0 flex flex-col items-center justify-center gap-2">
          <div className="rounded-t-[16px] md:bg-lightPurple w-full md:mb-4 md:py-8 flex items-center justify-center">
            <img className="mb-3 md:h-[140px] md:w-[140px] mx-auto" src={inviteMemberIcon} alt="invite member" />
          </div>

          <p className="md:px-7 font-bold text-xl text-center leading-[24.3px] md:mb-[0.15rem]">
            Invite people to this Budget
          </p>
          <p className="md:px-7 text-sm text-secondary text-center leading-[17px]">
            Enter the email address of the person you want to invite below
          </p>

          <div className="w-full md:px-5 mt-2">
            <input
              type="text"
              value={emailInput}
              onChange={(event) => handleInputChange(event.target.value)}
              placeholder="separate with comma"
              className={`bg-lightGray rounded-[20px] border w-full font-medium text-sm md:text-base p-[0.585rem] md:p-[0.45rem] md:px-[0.875rem] px-4 outline-none placeholder:text-[#93999F] text-[#495057] focus:border-[#ADB5BD] ${
                error ? 'border-red-500' : 'border-transparent'
              }`}
              disabled={loading}
            />
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          </div>
        </div>

        <div className="flex md:gap-4 md:px-5 md:mb-6 md:mt-6">
          <Button
            onClick={onClose}
            className="w-full !rounded-none font-semibold h-[52px] md:text-[1rem] md:font-semibold md:!rounded-full md:h-[44px]"
            variant="primary-reverse"
            disabled={loading}
            type="button"
          >
            Cancel
          </Button>

          <Button
            onClick={handleInvite}
            className="w-full text-base font-semibold h-[52px] !rounded-none md:text-[1rem] md:font-semibold md:!rounded-full md:h-[44px]"
            variant="default"
            disabled={loading}
            type="button"
          >
            {loading ? 'Inviting...' : 'Invite'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
